import React from 'react';
import { FaSearch, FaExclamationTriangle, FaUserSlash } from 'react-icons/fa';
import { formatListTimestamp, conversationTitle, formatAuPhone } from './conversationFormat';

// Sidebar of SMS threads, newest activity first. Selection is a radio group of buttons so the
// keyboard and screen-reader experience matches what the highlight implies visually.

const UnreadBadge = ({ count }) => (
  <span
    className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-jims-cyan text-white text-xs font-bold"
    aria-label={`${count} unread`}
  >
    {count > 99 ? '99+' : count}
  </span>
);

const ConversationRow = ({ conversation, isSelected, onSelect }) => {
  const title = conversationTitle(conversation);
  const hasUnread = conversation.unreadCount > 0;

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(conversation)}
        aria-current={isSelected ? 'true' : undefined}
        className={`w-full text-left px-4 py-3 border-l-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jims-cyan ${
          isSelected
            ? 'border-jims-cyan bg-jims-cyan/5'
            : 'border-transparent hover:bg-gray-50'
        }`}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className={`truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>
            {title}
          </span>
          <span className="shrink-0 text-xs text-gray-400">{formatListTimestamp(conversation.lastMessageAt)}</span>
        </div>

        <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
          {conversation.fseCode && <span className="shrink-0 font-medium text-gray-600">{conversation.fseCode}</span>}
          {!conversation.isKnownSender && (
            <span className="shrink-0 inline-flex items-center gap-1 text-amber-700" title="This number is not on any franchisee record">
              <FaUserSlash className="h-3 w-3" /> Unknown
            </span>
          )}
          {conversation.optedOut && (
            <span className="shrink-0 inline-flex items-center gap-1 text-red-700" title="This number replied STOP">
              <FaExclamationTriangle className="h-3 w-3" /> Opted out
            </span>
          )}
          <span className="truncate">{conversation.isKnownSender ? formatAuPhone(conversation.phoneE164) : ''}</span>
        </div>

        <div className="mt-1 flex items-center justify-between gap-2">
          <span className={`truncate text-sm ${hasUnread ? 'text-gray-800' : 'text-gray-500'}`}>
            {conversation.lastMessageDirection === 'outbound' && <span className="text-gray-400">You: </span>}
            {conversation.lastMessageText || <span className="italic text-gray-400">No messages yet</span>}
          </span>
          {hasUnread && <UnreadBadge count={conversation.unreadCount} />}
        </div>
      </button>
    </li>
  );
};

// A broadcast writes an outbound message to every recipient's thread, so after one send the list holds
// every franchisee. This filter is how an operator gets back to just the ones who actually replied.
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
];

const ConversationList = ({
  conversations,
  selectedId,
  onSelect,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  unreadCount,
  loading,
  truncated,
}) => (
  <div className="flex flex-col h-full">
    <div className="p-3 border-b border-gray-200 space-y-2">
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 w-full" role="group" aria-label="Filter conversations">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-pressed={filter === key}
            onClick={() => onFilterChange(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan ${
              filter === key ? 'bg-jims-cyan text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
            {key === 'unread' && unreadCount > 0 && (
              <span className={`text-xs font-bold ${filter === key ? 'text-white' : 'text-jims-cyan'}`}>
                ({unreadCount})
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search name, code or number…"
          aria-label="Search conversations"
          className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan"
        />
      </div>
    </div>

    <div className="flex-1 overflow-y-auto">
      {loading && conversations.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-500">Loading conversations…</p>
      ) : conversations.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">
          {search
            ? 'No conversations match your search.'
            : filter === 'unread'
            ? 'Nothing unread. Every reply has been read.'
            : 'No SMS conversations yet. Replies from franchisees will appear here.'}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {conversations.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              isSelected={conversation.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
      {truncated && (
        <p className="px-4 py-3 text-xs text-gray-400 border-t border-gray-100">
          Showing the most recent conversations only.
        </p>
      )}
    </div>
  </div>
);

export default ConversationList;
